import { useEffect, useState } from 'react';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { campaignCreationSteps } from 'modules/campaign-creation/stores/CampaignCreationSteps';
import { Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetEditCampaignQuery } from 'generated-graphql/hooks/apollo-hooks';
import { v4 as uuidv4 } from 'uuid';
import { ClosingMethod, QuestionType } from 'services/campaign/types';
import { Tone } from 'generated-graphql/types';
import { useQuery } from 'services/api/hooks';
import { getAutomationTasksByCampaignId } from 'services/automation/api';
import { AutomationTask } from 'services/automation/types';
import { CampaignStatus } from 'generated-graphql/types';
import { Paths } from 'services/navigation/paths';

export const CampaignCreationV3: React.FC = () => {
  const [hasRendered, setHasRendered] = useState(false);
  const step = useCampaignCreationStore((state) => state.step);
  const onNewCampaign = useCampaignCreationStore(
    (state) => state.onNewCampaign
  );
  const onEditCampaign = useCampaignCreationStore(
    (state) => state.onEditCampaign
  );
  const navigate = useNavigate();
  const { id: campaignId } = useParams();
  const { data: campaign } = useGetEditCampaignQuery({
    variables: {
      id: campaignId || ''
    },
    skip: !campaignId
  });

  const { data: automationTasks, isLoading: isAutomationTasksLoading } =
    useQuery<AutomationTask[]>(getAutomationTasksByCampaignId, {
      campaignId
    });

  useEffect(() => {
    if (campaignId) {
      return;
    }
    onNewCampaign();
    setHasRendered(true);
  }, [campaignId]);

  useEffect(() => {
    const campaignData = campaign?.getCampaign;

    // NOTE: Campaign must be draft to be edited
    if (campaignData && campaignData.status !== CampaignStatus.Draft) {
      navigate(`${Paths.OUTBOUND_CAMPAIGN}/${campaignId}`, {
        replace: true
      });
      return;
    }

    if (
      !campaignData ||
      !campaignId ||
      !campaignData.type ||
      isAutomationTasksLoading
    ) {
      return;
    }

    onEditCampaign({
      campaignId,
      state: {
        isDuplicate: campaignData.isDuplicate,
        campaignType: campaignData.type,
        engagementDescriptionState: campaignData.outreachDescription
          ? {
              outreachDescription: campaignData.outreachDescription,
              title: campaignData.title || null
            }
          : null,
        noATSJobDescriptionState:
          campaignData.jobDescriptionText && !campaignData.atsJobId
            ? {
                jobDescriptionText: campaignData.jobDescriptionText,
                externalId: campaignData.externalId || null
              }
            : null,
        ATSJobDescriptionState:
          campaignData.jobDescriptionText && campaignData.atsJobId
            ? {
                selectedJob: {
                  jobDescriptionText: campaignData.jobDescriptionText,
                  atsJobId: campaignData.atsJobId,
                  atsJobPostingId: campaignData.atsJobPostingId || null,
                  atsQuestions: null
                }
              }
            : null,
        jobDescriptionParsedState: campaignData.jobDescriptionParsed
          ? {
              jobDescriptionParsed: campaignData.jobDescriptionParsed,
              additionalContext: campaignData.additionalContext || null
            }
          : null,
        ATSImportState: campaignData.atsListId
          ? {
              atsListId: campaignData.atsListId
            }
          : null,
        CSVImportState: campaignData.csvS3Key
          ? {
              csvS3Key: campaignData.csvS3Key,
              csvColumns: {
                firstName: 'first-name',
                lastName: 'last-name',
                phoneNumber: 'phone-number',
                email: 'email'
              }
            }
          : null,
        questions: campaignData.questions
          ? campaignData.questions.map((question, i) => ({
              id: uuidv4(),
              content: question.content,
              // TODO: Fix this type
              questionType: question.questionType as unknown as QuestionType,
              isMandatory: question.isMandatory || false,
              documentTypes: question.documentTypes || [],
              hasPreferredAnswer: question.hasPreferredAnswer || false,
              preferredAnswerRequirements:
                question.preferredAnswerRequirements || [],
              preferredAnswerText: question.preferredAnswerText || null,
              expectedAnswer: question.expectedAnswer || null,
              order: question.order || i
            }))
          : null,
        channel: campaignData.channel || null,
        // ?? not || — an explicit false must survive the round-trip
        emailChannelFallbackEnabled:
          campaignData.emailChannelFallbackEnabled ?? null,
        language: campaignData.language || null,
        setupAndSimulationFields:
          campaignData.timeToAutoCloseConversationsInHours
            ? {
                agentId: campaignData.agent?.id || '',
                agentName: campaignData.agent?.name || '',
                agentTone: campaignData.agent?.tone || Tone.Neutral,
                // TODO: Fix this type
                closingMethod:
                  campaignData.closingMethod as unknown as ClosingMethod,
                templateId: campaignData.templateId || null,
                customTemplatedMessage:
                  campaignData.customTemplatedMessage || null,
                customTemplatedSubjectLine:
                  campaignData.customTemplatedSubjectLine || null,
                calendarMeetingTemplateId:
                  campaignData.calendarMeetingTemplateId || null,
                customRequest: campaignData.customRequest || null,
                rejectionMessage: campaignData.rejectionMessage || null,
                closingMessage: campaignData.closingMessage || null,
                timeToAutoCloseConversationsInHours:
                  campaignData.timeToAutoCloseConversationsInHours || null,
                disableNudging: campaignData.disableNudging ?? false,
                closePreviousConversations: false
              }
            : null,
        automationTasks: automationTasks || null
      }
    });
    setHasRendered(true);
  }, [campaign, isAutomationTasksLoading]);

  const Content = campaignCreationSteps[step];

  // NOTE: This prevents old stale steps from being rendered
  if (!hasRendered) {
    return null;
  }

  return (
    <Stack>
      <Content />
    </Stack>
  );
};
